import { useI18n } from "../i18n/useI18n";
import { numberLocaleFor } from "../i18n/locale";
import type { ScalingPoint } from "../lib/types";

type Props = {
  points: ScalingPoint[];
  currentUsers: number;
};

export function ScalingChart({ points, currentUsers }: Props) {
  const { locale, t, tf } = useI18n();
  const nf = numberLocaleFor(locale);
  const maxCost = Math.max(...points.map((p) => p.totalMonthlyUsd), 1);

  return (
    <div>
      <h2>{t("scaling.title")}</h2>
      <p className="sub">{t("scaling.sub")}</p>
      <div className="chart" role="img" aria-label={t("scaling.chartAria")}>
        {points.map((p, i) => {
          const h = (p.totalMonthlyUsd / maxCost) * 100;
          const isNear =
            Math.abs(p.usersDaily - currentUsers) / Math.max(currentUsers, 1) < 0.15;
          const usersStr = p.usersDaily.toLocaleString(nf);
          const usd = Math.round(p.totalMonthlyUsd);
          return (
            <div key={i} className="bar-wrap">
              <div
                className={isNear ? "bar highlight" : "bar"}
                style={{ height: `${Math.max(h, 4)}%` }}
                title={tf("scaling.barTitle", { users: usersStr, usd })}
              />
              <span className="x">{shortUsers(p.usersDaily)}</span>
            </div>
          );
        })}
      </div>
      <div className="legend">
        <span>{tf("scaling.legend", { dau: currentUsers.toLocaleString(nf) })}</span>
      </div>

      <style>{`
        h2 {
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
        .sub {
          margin: 0 0 0.85rem;
          font-size: 0.8rem;
          color: var(--muted);
        }
        .chart {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 0.35rem;
          height: 140px;
          padding: 0 0.25rem;
          border-bottom: 1px solid var(--border);
        }
        .bar-wrap {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
          min-width: 0;
        }
        .bar {
          width: 100%;
          max-width: 48px;
          margin-top: auto;
          background: linear-gradient(180deg, var(--accent-dim), #3d3518);
          border-radius: 4px 4px 0 0;
          transition: height 0.2s ease;
        }
        .bar.highlight {
          background: linear-gradient(180deg, var(--accent), #8a7020);
          box-shadow: 0 0 0 1px var(--accent);
        }
        .x {
          font-size: 0.65rem;
          color: var(--muted);
          margin-top: 0.35rem;
          font-family: var(--mono);
        }
        .legend {
          margin-top: 0.5rem;
          font-size: 0.75rem;
          color: var(--muted);
        }
      `}</style>
    </div>
  );
}

function shortUsers(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}k`;
  return String(n);
}
